import { Badge, Banner, Button, Dialog, Field, Input, Switch } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { BrandLogo } from '../../components/BrandLogo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable as Table } from '../../components/ui/DataTable';
import { ICONS, Glyph } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { ADDON_TYPES, BILLING_LABEL, TOOL_BRANDS, TOOL_ICONS, brlFromCents, knownTools, type AddonType, type ToolsOverview } from '../../lib/tools';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { SkeletonText } from '../../components/ui/Skeleton';

interface Transaction { id: string; amountCents: number; kind: string; description: string; createdAt: string }
interface Refundable { id: string; amountCents: number; refundableCents: number; createdAt: string; deadline: string; walletLabel?: string }

const KIND_LABEL: Record<string, string> = { PURCHASE: 'Compra', USAGE: 'Uso', REFUND: 'Estorno', ADJUST: 'Ajuste', WITHDRAWAL: 'Reembolso' };

/** Ferramentas: catálogo, carteira de créditos e add-ons mensais */
export function FerramentasPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<ToolsOverview | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [buyOpen, setBuyOpen] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [notice, setNotice] = useState<{ variant: 'success' | 'info'; text: string } | null>(null);
  const [refundable, setRefundable] = useState<Refundable[]>([]);
  const [prepaidQty, setPrepaidQty] = useState<Record<string, number>>({});
  const [refunding, setRefunding] = useState<Refundable | null>(null);

  const canBuy = can('billing:manage');
  const canSeeStatement = can('billing:read');

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools');
    if (!res.ok) return;
    const d: ToolsOverview = await res.json();
    setData(d);
    setQty(d.contracted);
    setPrepaidQty({});
    if (canSeeStatement) {
      const t = await fetchApi('/api/tools/credits/transactions');
      if (t.ok) setTransactions((await t.json()).transactions || []);
      const r = await fetchApi('/api/tools/credits/refunds');
      if (r.ok) setRefundable(((await r.json()).purchases || []).filter((x: Refundable) => x.refundableCents > 0));
    }
  }, [fetchApi, canSeeStatement]);
  useEffect(() => { load(); }, [load]);

  // Links como /ferramentas#addons e #creditos: rola até a seção depois de carregar
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  useEffect(() => {
    if (!data || !hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    return () => clearTimeout(t);
  }, [data, hash]);

  useEffect(() => {
    const r = searchParams.get('creditos');
    if (!r) return;
    setNotice(r === 'sucesso'
      ? { variant: 'success', text: 'Pagamento enviado. Os créditos entram assim que o Stripe confirmar (no Pix, após o pagamento do QR Code).' }
      : { variant: 'info', text: 'Compra cancelada; nada foi cobrado.' });
    searchParams.delete('creditos');
    setSearchParams(searchParams, { replace: true });
    if (r === 'sucesso') {
      let n = 0;
      const timer = setInterval(() => { n += 1; load(); if (n >= 6) clearInterval(timer); }, 3000);
      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async () => {
    const cents = amount ?? Math.round(Number(custom.replace(',', '.')) * 100);
    if (!data || !Number.isInteger(cents) || cents < data.pricing.minCreditCents) {
      toast.error(`Valor mínimo de ${brlFromCents(data?.pricing.minCreditCents ?? 2000)}`);
      return;
    }
    setBusy('buy');
    try {
      const res = await fetchApi('/api/tools/credits/checkout', { method: 'POST', body: JSON.stringify({ amountCents: cents }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível iniciar o pagamento');
      window.location.assign(body.url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível iniciar o pagamento');
      setBusy(null);
    }
  };

  // Direito de arrependimento (CDC, art. 49): compras de até 7 dias, no valor não usado
  const confirmRefund = async () => {
    const r = refunding;
    setRefunding(null);
    if (!r) return;
    setBusy(`refund-${r.id}`);
    try {
      const res = await fetchApi('/api/tools/credits/refund', { method: 'POST', body: JSON.stringify({ transactionId: r.id }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível reembolsar');
      toast.success(`${brlFromCents(body.refundedCents)} devolvidos ao meio de pagamento da compra`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível reembolsar');
    } finally {
      setBusy(null);
    }
  };

  const savePrepaid = async (type: AddonType, quantity: number) => {
    setBusy(`pre-${type}`);
    try {
      const res = await fetchApi(`/api/tools/addons/${type}/credits`, { method: 'PUT', body: JSON.stringify({ quantity }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível alterar');
      toast.success(quantity > 0 ? `Liberado até ${new Date(body.until).toLocaleDateString('pt-BR')}` : 'Add-on encerrado; a diferença voltou para a carteira');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível alterar');
    } finally {
      setBusy(null);
    }
  };

  const toggleRenew = async (type: AddonType, autoRenew: boolean) => {
    await fetchApi(`/api/tools/addons/${type}/credits`, { method: 'PATCH', body: JSON.stringify({ autoRenew }) });
    load();
  };

  const saveAddon = async (type: AddonType) => {
    setBusy(type);
    try {
      const res = await fetchApi(`/api/tools/addons/${type}`, { method: 'PUT', body: JSON.stringify({ quantity: qty[type] ?? 0 }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || 'Não foi possível alterar o add-on');
      toast.success('Add-on atualizado na assinatura');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível alterar o add-on');
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return <div className="py-8"><SkeletonText lines={4} /></div>;
  }


  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Ferramentas</h2>
        <div style={{ color: 'var(--text-muted)' }}>Recursos extras para melhorar a entrega das suas campanhas.</div>
      </div>

      <div className="page-content space-y-6">
        {notice && <Banner variant="default" description={<>{notice.text}</>} />}

        <div className="tools-grid">
          {knownTools(data.tools).map((t) => (
            <div key={t.id} className="tools-card">
              <div className="flex items-center justify-between gap-2">
                <span className="tools-launcher__icon">{TOOL_BRANDS[t.id] ? <BrandLogo brand={TOOL_BRANDS[t.id]!} size={20} rounded={4} /> : <Glyph icon={TOOL_ICONS[t.id]} size={16}/>}</span>
                {t.ready ? (t.active ? <Badge variant="green">Ativo</Badge> : <Badge variant="neutral">{BILLING_LABEL[t.billing]}</Badge>) : <Badge variant="blue">Em breve</Badge>}
              </div>
              <div className="font-semibold" style={{ marginTop: 12 }}>{t.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, flex: 1 }}>{t.description}</div>
              <div style={{ marginTop: 12, alignSelf: 'flex-start' }}>
                <Button variant="secondary" icon={ICONS.ArrowRight} disabled={!t.ready} onClick={() => navigate(`/ferramentas/${t.id}`)}>
                  Abrir
                </Button>
              </div>
            </div>
          ))}
        </div>

        <section className="brand-card" id="creditos">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h4 className="text-base font-semibold">Créditos das ferramentas</h4>
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
                Usados nos add-ons pagos com créditos, como as vagas da API oficial da Meta.
              </div>
            </div>
            <div className="tools-balance">
              <small>Saldo</small>
              <strong>{brlFromCents(data.balanceCents)}</strong>
            </div>
          </div>
          {canBuy && (
            <div style={{ marginTop: 16 }}>
              <Button variant="primary" icon={ICONS.Plus} disabled={!data.stripeConfigured} onClick={() => { setAmount(data.pricing.creditPacksCents[0] ?? null); setCustom(''); setBuyOpen(true); }}>
                Comprar créditos
              </Button>
              {!data.stripeConfigured && <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 8 }}>A cobrança online ainda não foi ativada na plataforma.</div>}
            </div>
          )}
          {canBuy && refundable.length > 0 && (
            <div className="tools-refunds">
              <div className="font-semibold">Arrependimento (até 7 dias)</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Pelo Código de Defesa do Consumidor, você pode desistir de uma compra de créditos em até 7 dias. Devolvemos o valor ainda não usado.</div>
              <ul className="account-list" style={{ marginTop: 8 }}>
                {refundable.map((r) => (
                  <li key={r.id}>
                    <ICONS.Undo size={16} />
                    <div>
                      <strong>Compra de {brlFromCents(r.amountCents)} em {new Date(r.createdAt).toLocaleDateString('pt-BR')}{r.walletLabel ? ` · ${r.walletLabel}` : ''}</strong>
                      <span>Reembolsável: {brlFromCents(r.refundableCents)} · até {new Date(r.deadline).toLocaleDateString('pt-BR')}</span>
                    </div>
                    <Button variant="secondary" loading={busy === `refund-${r.id}`} onClick={() => setRefunding(r)}>Reembolsar</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canSeeStatement && transactions.length > 0 && (
            <div className="bg-white rounded-lg border border-[var(--border)] overflow-hidden" style={{ marginTop: 16 }}>
              <Table
                variant="borderless"
                items={transactions.slice(0, 20)}
                columnDefinitions={[
                  {
                    id: 'data',
                    header: 'Data',
                    cell: (t: Transaction) => <div className="text-xs !text-[var(--text-muted)]">{new Date(t.createdAt).toLocaleString('pt-BR')}</div>,
                  },
                  {
                    id: 'tipo',
                    header: 'Tipo',
                    cell: (t: Transaction) => <Badge variant={t.amountCents > 0 ? 'green' : 'neutral'}>{KIND_LABEL[t.kind] || t.kind}</Badge>,
                  },
                  {
                    id: 'descricao',
                    header: 'Descrição',
                    cell: (t: Transaction) => <div>{t.description}</div>,
                  },
                  {
                    id: 'valor',
                    header: 'Valor',
                    cell: (t: Transaction) => (
                      <div style={{ color: t.amountCents > 0 ? 'var(--brand-text)' : 'var(--text)', fontWeight: 600 }}>
                        {t.amountCents > 0 ? '+' : ''}{brlFromCents(t.amountCents)}
                      </div>
                    ),
                  },
                ]}
                ariaLabels={{ tableLabel: 'Extrato de créditos', selectionGroupLabel: 'Extrato de créditos' }}
              />
            </div>
          )}
        </section>

        <section className="brand-card" id="addons">
          <h4 className="text-base font-semibold">Add-ons mensais</h4>
          <div style={{ color: 'var(--text-muted)', marginTop: 2, marginBottom: 12 }}>
            Pague com os créditos da carteira: 30 dias por vez, com renovação automática enquanto houver saldo. Mudar a quantidade no meio do período cobra ou devolve só a diferença dos dias restantes.
          </div>
          <ul className="account-list" style={{ marginTop: 0 }}>
            {ADDON_TYPES.map((type) => {
              const a = data.pricing.addons[type];
              const pre = data.prepaid[type];
              const value = prepaidQty[type] ?? pre.quantity;
              const changed = value !== pre.quantity;
              const unit = Math.round(a.monthly * 100);
              const courtesy = data.addons[type] - data.contracted[type] - pre.quantity;
              return (
                <li key={type}>
                  <ICONS.Shield size={16} />
                  <div>
                    <strong>{a.label}</strong>
                    <span>
                      {brlFromCents(unit)} por 30 dias cada · liberados: {data.addons[type]}
                      {pre.quantity > 0 && pre.until ? ` · ${pre.quantity} pagos com créditos até ${new Date(pre.until).toLocaleDateString('pt-BR')}` : ''}
                      {data.contracted[type] > 0 ? ` · ${data.contracted[type]} na assinatura` : ''}
                      {courtesy > 0 ? ` · ${courtesy} de cortesia` : ''}
                    </span>
                    {pre.quantity > 0 && canBuy && (
                      <label className="flex items-center gap-2" style={{ marginTop: 6 }}>
                        <Switch onCheckedChange={(checked: boolean) => toggleRenew(type, checked)} aria-label="Renovar automaticamente" checked={pre.autoRenew} />
                        <span style={{ fontSize: 12 }}>Renovar automaticamente com créditos</span>
                      </label>
                    )}
                  </div>
                  {canBuy && pre.quantity === 0 && !changed && (
                    <div className="tools-qty-controls">
                      <Button variant="primary" icon={ICONS.Plus} disabled={unit <= 0} loading={busy === `pre-${type}`} onClick={() => savePrepaid(type, 1)}>
                        Contratar por {brlFromCents(unit)}
                      </Button>
                    </div>
                  )}
                  {canBuy && (pre.quantity > 0 || changed) && (
                    <div className="tools-qty-controls">
                      <Button variant="ghost" shape="square" icon={ICONS.Minus} aria-label="Diminuir" disabled={value <= 0} onClick={() => setPrepaidQty({ ...prepaidQty, [type]: Math.max(0, value - 1) })} />
                      <span className="tools-qty" aria-live="polite">{value}</span>
                      <Button variant="ghost" shape="square" icon={ICONS.Plus} aria-label="Aumentar" disabled={unit <= 0} onClick={() => setPrepaidQty({ ...prepaidQty, [type]: value + 1 })} />
                      <Button variant="primary" disabled={!changed} loading={busy === `pre-${type}`} onClick={() => savePrepaid(type, value)}>
                        {changed && value > pre.quantity && pre.quantity === 0 ? `Pagar ${brlFromCents(unit * value)}` : 'Salvar'}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {data.stripeConfigured && data.hasSubscription && canBuy && (
            <div style={{ marginTop: 16 }}>
              <div className="font-semibold">Na assinatura (cartão ou Pix mensal)</div>
              <ul className="account-list" style={{ marginTop: 4 }}>
                {ADDON_TYPES.map((type) => {
                  const a = data.pricing.addons[type];
                  const value = qty[type] ?? 0;
                  return (
                    <li key={type}>
                      <ICONS.CreditCard size={16} />
                      <div><strong>{a.label}</strong><span>{data.contracted[type]} na assinatura</span></div>
                      <div className="tools-qty-controls">
                        <Button variant="ghost" shape="square" icon={ICONS.Minus} aria-label="Diminuir" disabled={value <= 0} onClick={() => setQty({ ...qty, [type]: Math.max(0, value - 1) })} />
                        <span className="tools-qty">{value}</span>
                        <Button variant="ghost" shape="square" icon={ICONS.Plus} aria-label="Aumentar" disabled={!a.purchasable} onClick={() => setQty({ ...qty, [type]: value + 1 })} />
                        <Button variant="secondary" disabled={value === data.contracted[type]} loading={busy === type} onClick={() => saveAddon(type)}>Salvar</Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>
      </div>

      <DangerConfirmModal
        open={refunding !== null}
        title="Reembolsar créditos"
        confirmText="Reembolsar"
        requiredText="REEMBOLSAR"
        onConfirm={confirmRefund}
        onCancel={() => setRefunding(null)}
      >
        {refunding ? `${brlFromCents(refunding.refundableCents)} saem da carteira e voltam para o cartão ou a conta usada no Pix. O prazo do banco para o valor aparecer é de até 10 dias úteis.` : ''}
      </DangerConfirmModal>

      <Dialog.Root open={buyOpen} onOpenChange={(o: boolean) => { if (!o) (() => { if (buyOpen) setBuyOpen(false); })(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold" style={{ marginBottom: 4 }}>Comprar créditos</h3>}</Dialog.Title>

        <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>Pagamento único, com cartão ou Pix. Os créditos não expiram.</div>
        <div className="tools-packs">
          {data.pricing.creditPacksCents.map((c) => (
            <button key={c} type="button" className={`tools-pack ${amount === c ? 'tools-pack--active' : ''}`} onClick={() => { setAmount(c); setCustom(''); }}>
              {brlFromCents(c)}
            </button>
          ))}
        </div>
        <Field label="Outro valor (R$)">
          <Input onChange={(e) => { setCustom(e.target.value); setAmount(null); }} inputMode="decimal" placeholder={`Mínimo ${brlFromCents(data.pricing.minCreditCents)}`} value={custom} />
        </Field>
        <div className="flex justify-end gap-2" style={{ marginTop: 24 }}>
          <Button variant="secondary" onClick={() => setBuyOpen(false)}>Cancelar</Button>
          <Button variant="primary" loading={busy === 'buy'} onClick={buy}>Ir para o pagamento</Button>
        </div>
      
</Dialog>
</Dialog.Root>
      <p style={{ padding: '0 40px 24px', fontSize: 11, color: 'var(--text-faint)' }}>
        <a href="https://logo.dev" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>Logos por Logo.dev</a>
      </p>
    </>
  );
}
